/**
 * RequestDispensaryPanel — the "Request a dispensary" tab on the
 * Dispensaries page. Sales rep types a full street address, gets up to
 * 3 strict matches back, and submits a request to be assigned. Admins
 * see the request in their queue and approve/reject.
 *
 * Their own pending/decided requests stack below so they can track
 * what's outstanding without leaving the page.
 */
import React, { useEffect, useState } from "react";
import {
  Search,
  MapPin,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Send,
} from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  searchDispensaryByAddress,
  requestDispensaryAssignment,
  fetchMyAssignmentRequests,
} from "../lib/api";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export function RequestDispensaryPanel() {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState(null);
  const [myRequests, setMyRequests] = useState(null);

  const loadRequests = async () => {
    try {
      const rows = await fetchMyAssignmentRequests();
      setMyRequests(rows);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Could not load my requests", err);
      setMyRequests([]);
    }
  };
  useEffect(() => {
    loadRequests();
  }, []);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!query.trim()) return;
    setSearching(true);
    setError(null);
    setMatches(null);
    try {
      const res = await searchDispensaryByAddress(query.trim());
      setMatches(res.matches || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Search failed.");
      setMatches([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div data-testid="request-panel" className="flex flex-col gap-5">
      {/* Search */}
      <div className="panel p-4">
        <div className="flex items-start gap-2 mb-3">
          <MapPin className="w-4 h-4 text-sky-400 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-zinc-50">
              Find a dispensary by address
            </h3>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
              Type the <strong className="text-zinc-300">full street
              address with the city</strong> (e.g.{" "}
              <span className="font-mono text-zinc-300">
                2700 Shannon St, Santa Ana
              </span>
              ). The system only shows you the matching shop — not the whole
              DCC list. Small typos are okay.
            </p>
          </div>
        </div>

        <form onSubmit={runSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              data-testid="address-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="123 Main St, San Francisco"
              className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100"
            />
          </div>
          <Button
            type="submit"
            data-testid="address-search-btn"
            disabled={searching || !query.trim()}
            className="bg-sky-600 hover:bg-sky-500 text-sky-50"
          >
            {searching ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Search className="w-4 h-4 mr-1.5" />
                Search
              </>
            )}
          </Button>
        </form>

        {error && (
          <div
            data-testid="search-error"
            className="mt-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2 flex items-start gap-2"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        {matches !== null && matches.length === 0 && !error && (
          <div
            data-testid="no-match"
            className="mt-3 text-xs text-zinc-400 bg-zinc-900/60 border border-zinc-800 rounded px-3 py-3"
          >
            No exact match — please check the street number and city, then try
            again.
          </div>
        )}

        {matches && matches.length > 0 && (
          <ul
            data-testid="match-list"
            className="mt-3 flex flex-col gap-2"
          >
            {matches.map((m) => (
              <MatchRow key={m.id} match={m} onSubmitted={loadRequests} />
            ))}
          </ul>
        )}
      </div>

      {/* My recent requests */}
      <div className="panel p-4">
        <h3 className="text-[11px] uppercase tracking-wider font-mono text-zinc-500 mb-2">
          Your recent requests
        </h3>
        {myRequests === null ? (
          <div className="text-xs text-zinc-500 font-mono">Loading…</div>
        ) : myRequests.length === 0 ? (
          <p className="text-xs italic text-zinc-600">
            You haven't requested any dispensaries yet.
          </p>
        ) : (
          <ul data-testid="my-requests" className="flex flex-col gap-1.5">
            {myRequests.map((r) => (
              <li
                key={r.id}
                data-testid={`my-request-${r.id}`}
                className="text-xs flex items-center gap-3 bg-zinc-900/60 border border-zinc-800 rounded px-3 py-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-100 truncate">
                    {r.dispensary_name || "(unknown)"}
                  </div>
                  <div className="text-[10px] text-zinc-500 font-mono truncate">
                    {r.dispensary_address} · {r.dispensary_city} ·{" "}
                    {fmtDate(r.created_at)}
                  </div>
                  {r.note && (
                    <div className="text-[10px] text-zinc-500 italic mt-0.5 truncate">
                      "{r.note}"
                    </div>
                  )}
                </div>
                <StatusBadge status={r.status} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "approved")
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border bg-emerald-500/15 border-emerald-500/30 text-emerald-300">
        <CheckCircle2 className="w-3 h-3" />
        Approved
      </span>
    );
  if (status === "rejected")
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border bg-rose-500/15 border-rose-500/30 text-rose-300">
        <XCircle className="w-3 h-3" />
        Rejected
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border bg-amber-500/15 border-amber-500/30 text-amber-300">
      <Clock className="w-3 h-3" />
      Pending
    </span>
  );
}

function MatchRow({ match, onSubmitted }) {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(match.my_request_pending);

  const submit = async () => {
    setSubmitting(true);
    try {
      await requestDispensaryAssignment(match.id, note.trim() || null);
      toast.success(`Requested ${match.name}`, {
        description: "An admin will review your request.",
      });
      setSubmitted(true);
      onSubmitted?.();
    } catch (err) {
      toast.error("Could not submit request", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <li
      data-testid={`match-${match.id}`}
      className="bg-zinc-950/60 border border-zinc-800 rounded p-3 flex flex-col gap-2"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-zinc-50 truncate">
              {match.name}
            </h4>
            <span className="text-[10px] font-mono text-zinc-500">
              match {Math.round((match.match_score || 0) * 100)}%
            </span>
          </div>
          <div className="text-xs text-zinc-400 mt-0.5">
            {match.address}
            {match.city ? `, ${match.city}` : ""}
            {match.zip ? ` ${match.zip}` : ""}
          </div>
          {match.current_assignee_label && (
            <div className="text-[11px] text-amber-300 mt-1 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Currently assigned to{" "}
              <span className="font-medium">{match.current_assignee_label}</span> —
              an admin will see this clearly when reviewing your request.
            </div>
          )}
        </div>
        {submitted ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded border bg-amber-500/15 border-amber-500/30 text-amber-300">
            <Clock className="w-3 h-3" />
            Pending admin approval
          </span>
        ) : (
          <Button
            data-testid={`request-btn-${match.id}`}
            onClick={submit}
            disabled={submitting}
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 shrink-0"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Request this account
              </>
            )}
          </Button>
        )}
      </div>
      {!submitted && (
        <Textarea
          data-testid={`note-${match.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note for the admin (e.g. 'spoke with the buyer last week')"
          rows={2}
          className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm resize-none"
        />
      )}
    </li>
  );
}
